import { StreetSegment } from "./street-segment.model";
import { SegmentFeature } from "../types/segment-feature.type";
import { SurfaceMetadata } from "./surface-metadata.model";
import { MarkingsMetadata } from "./markings-metadata.model";
import { StreetSegmentStatusKind } from "../enum/street-segment-status-kind.enum";

export class StreetSegmentStatus {
    constructor(
        public id: number,
        public streetSegment: StreetSegment,
        public index: number,
        public readonly kind: StreetSegmentStatusKind,
    ) { }

    static fromObject(obj: {
        id: number,
        streetSegment: StreetSegment,
        metadata: any,
        kind: StreetSegmentStatusKind,
        index: number,
    }): StreetSegmentStatus {
        const streetSegment = StreetSegment.fromObject(obj.streetSegment);
        switch (obj.kind) {
            case StreetSegmentStatusKind.SURFACE:
                return new StreetSegmentSurfaceStatus(obj.id, streetSegment, obj.index, obj.metadata);
            case StreetSegmentStatusKind.MARKINGS:
                return new StreetSegmentMarkingsStatus(obj.id, streetSegment, obj.index, obj.metadata);
            default:
                const _unreachable: never = obj.kind;
                throw new Error('Reached unreachable in StreetSegmentStatus');
        }
    }

    toFeature(): SegmentFeature {
        return {
            type: 'Feature',
            properties: {
                __type: 'SEGMENT_STATUS_BRAND',
                index: this.index,
                line_width: 2,
                segmentId: this.streetSegment.id,
            },
            geometry: this.streetSegment!.geometry,
        };
    }

    isSurfaceStatus(): this is StreetSegmentSurfaceStatus {
        return this.kind === StreetSegmentStatusKind.SURFACE;
    }

    isMarkingsStatus(): this is StreetSegmentSurfaceStatus {
        return this.kind === StreetSegmentStatusKind.MARKINGS;
    }

    assertSurfaceStatus(): asserts this is StreetSegmentSurfaceStatus {
        if (!this.isSurfaceStatus()) {
            throw new Error('Assertion error: status is not of Surface kind');
        }
    }

    assertMarkingsStatus(): asserts this is StreetSegmentSurfaceStatus {
        if (!this.isMarkingsStatus()) {
            throw new Error('Assertion error: status is not of Markings kind');
        }
    }
}

export class StreetSegmentSurfaceStatus extends StreetSegmentStatus {
    constructor(
        id: number,
        streetSegment: StreetSegment,
        index: number,
        public metadata: SurfaceMetadata,
    ) {
        super(id, streetSegment, index, StreetSegmentStatusKind.SURFACE);
    }
}

export class StreetSegmentMarkingsStatus extends StreetSegmentStatus {
    constructor(
        id: number,
        streetSegment: StreetSegment,
        index: number,
        public metadata: MarkingsMetadata,
    ) {
        super(id, streetSegment, index, StreetSegmentStatusKind.MARKINGS);
    }
}
